// React
import React, { FC, useEffect, useState } from "react";
import { setLocale } from "yup";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { compose, bindActionCreators, Dispatch } from "redux";
// Hooks
import { useFormatMessage } from "../../../../hooks/formatMessage";
import { useSwal } from "../../../../hooks/useSwal";
// Layout/Components
import { Flex } from "reflexbox";
import { Form } from "@components/Form";
import { FormInput as Input } from "@components/Form/Input";
import { FormSelect as Select } from "@components/Form/Select";
import { Button as OldButton, EButtonType } from "@components/OldButton";
import { Button } from "@components/Button";
// Models
import { ACTION } from "../../../../shared/models/action";
// Actions
import { TProps } from "./types";
import { ObjectRoleSchema } from "./schema";

import { createObjectRole, changeObjectRole } from "../ducks";
import { fetchRoles } from "../../../../actions/role";
import { fetchObjects } from "../../../../actions/object";
import { ReactComponent as IconArrowRight } from "../../../../assets/icons/arrow-right.svg";

setLocale({
  mixed: {
    default: "app.requiredField",
    required: "app.requiredField",
  },
  string: {
    required: "app.requiredField",
  },
  number: {
    required: "app.requiredField",
  },
  object: {
    required: "app.requiredField",
  },
});

const ObjectRolesModal: FC<TProps> = props => {
  // Props
  const { closeModal, initialData, fetchRoles, fetchObjects } = props;
  // Language
  const formatMessage = useFormatMessage();
  // Swal
  const { swalConfirmationMessage, swalAlertWithCallback } = useSwal();
  // Page control
  const [isEditing, setIsEditing] = useState<boolean>();
  // Lists
  const [rolesList, setRolesList] = useState<any>();
  const [objectsList, setObjectsList] = useState<any>();

  useEffect(() => {
    setIsEditing(!!initialData.id);
  }, [initialData.id]);

  useEffect(() => {
    document.body.classList.remove("backdrop");
  }, []);

  // Fetch lists
  useEffect(() => {
    fetchRoles().then(ret => setRolesList(ret));
    fetchObjects().then(ret => setObjectsList(ret));
  }, [fetchRoles, fetchObjects]);

  const showSuccessMessage = action => {
    swalAlertWithCallback(
      {
        text: `Object Role has been ${action}!`,
        type: "success",
        confirmationText: "Ok!",
      },
      closeModal
    );
  };

  const onSubmit = formData => {
    const objectRole = {
      ...formData,
      objectId: formData.objectId ? formData.objectId.value : undefined,
      roleId: formData.roleId ? formData.roleId.value : undefined,
    };

    if (isEditing) {
      swalConfirmationMessage({
        text: "Do you really want to change this object role?",
        // content: (),
        type: "info",
        cancelationText: "Cancel",
        confirmationText: "Save changes",
      }).then(value => {
        if (value) {
          changeObjectRole(formData.id, objectRole).then(res => {
            if (res.success) showSuccessMessage("changed");
          });
        }
      });
    } else {
      delete objectRole.id;
      swalConfirmationMessage({
        text: "Do you really want to create this object role?",
        type: "info",
        cancelationText: "Cancel",
        confirmationText: "Save new Object Role",
      }).then(value => {
        if (value) {
          createObjectRole(objectRole).then(res => {
            if (res.success) showSuccessMessage("created");
          });
        }
      });
    }
  };

  return (
    <div className="modal">
      <div className="modal-header">
        <div className="modal-header-left">
          <OldButton
            className="primary"
            onClick={closeModal}
            to={null}
            buttonType={EButtonType.ButtonText}
          >
            <div className="iconWrapper arrow left d-inline-block mr-3 small">
              <IconArrowRight />
            </div>
            {formatMessage("app.backToOverview", "Zurück zur Übersicht")}
          </OldButton>
        </div>
      </div>
      <Flex flexDirection="column" mb={20}>
        <Form onSubmit={onSubmit} schema={ObjectRoleSchema} defaultValues={initialData}>
          <Flex>
            <Input id="id" name="id" hidden />
            <Select
              id="objectId"
              name="objectId"
              label={formatMessage("app.object", "Object")}
              options={objectsList?.map(option => {
                return {
                  value: option.id,
                  label: option.objectName,
                };
              })}
            />
          </Flex>
          <Flex mt="30px">
            <Select
              id="roleId"
              name="roleId"
              label={formatMessage("app.role", "Role")}
              options={rolesList?.map(option => {
                return {
                  value: option.id,
                  label: option.roleName,
                };
              })}
            />
          </Flex>
          <Flex mt={20} width="100%" justifyContent="flex-end" pr="15px">
            <Button id="ModalBtn_cancel" textButton mr="10px" onClick={closeModal}>
              {formatMessage("app.cancel", "Cancel")}
            </Button>
            <Button id="ModalBtn_save" type="submit">
              {formatMessage("app.save", "Save")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </div>
  );
};

function mapDispatchToProps(dispatch: Dispatch<ACTION>) {
  return {
    fetchRoles: bindActionCreators(fetchRoles, dispatch),
    fetchObjects: bindActionCreators(fetchObjects, dispatch),
  };
}

const enhance = compose(connect(null, mapDispatchToProps), injectIntl);

export default enhance(ObjectRolesModal);
